import type {DateTimeUnit} from 'luxon';
import {DateTime} from 'luxon';

import {isNumberWeekDayNumber} from '../../../../../../libs/shared/core/src/helpers/datetime/luxonHelpers';
import {collectEnumValues} from '../../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {CalendarWeekDaysEnum} from '../../../../../models/api/calendarCommonTypes';

const weekdayNames: ReadonlyArray<string> = collectEnumValues(CalendarWeekDaysEnum);

const monthNames = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

export function formatPeriod(localTime: DateTime, unit: DateTimeUnit) {
  return {
    start: localTime.startOf(unit).valueOf(),
    end: localTime.endOf(unit).valueOf(),
  };
}

export function parsePeriod(value: string, timezone?: string, fullTimestampPrecision = false) {
  const effectiveTimezone = timezone || 'UTC';
  const timezoneOption = {zone: effectiveTimezone};
  const localTime = DateTime.local().setZone(effectiveTimezone);

  switch (value) {
    case 'today':
      return formatPeriod(localTime, 'day');

    case 'this-week':
      return formatPeriod(localTime, 'week');

    case 'this-month':
      return formatPeriod(localTime, 'month');

    case 'this-year':
      return formatPeriod(localTime, 'year');

    case 'yesterday':
      return formatPeriod(localTime.minus({day: 1}), 'day');

    case 'last-week':
      return formatPeriod(localTime.minus({week: 1}), 'week');

    case 'last-month':
      return formatPeriod(localTime.minus({month: 1}), 'month');

    case 'last-year':
      return formatPeriod(localTime.minus({year: 1}), 'year');

    default: {
      const weekdayIndex = weekdayNames.indexOf(value);
      if (weekdayIndex >= 0) {
        // Add one to get an ISO week day (Monday is 1, Sunday 7).
        const maybeWeekday = weekdayIndex + 1;
        const weekDay = isNumberWeekDayNumber(maybeWeekday) ? maybeWeekday : 1;
        const dayLocalTime = localTime.set({weekday: weekDay});
        return formatPeriod(dayLocalTime, 'day');
      }

      const monthIndex = monthNames.indexOf(value);
      if (monthIndex >= 0) {
        // Add one to get an ISO month.
        const monthLocalTime = localTime.set({month: monthIndex + 1});
        return formatPeriod(monthLocalTime, 'month');
      }

      // YYYY-MM-DD: date.
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const dateTime = DateTime.fromFormat(value, 'yyyy-MM-dd', timezoneOption);
        return formatPeriod(dateTime, 'day');
      }

      // YYYY-MM: date.
      if (/^\d{4}-\d{2}$/.test(value)) {
        const monthTime = DateTime.fromFormat(value, 'yyyy-MM', timezoneOption);
        return formatPeriod(monthTime, 'month');
      }

      // YYYY: year.
      if (/^\d{4}$/.test(value)) {
        const yearTime = DateTime.fromFormat(value, 'yyyy', timezoneOption);
        return formatPeriod(yearTime, 'year');
      }

      // Timestamp in seconds.
      if (/^\d{10}$/.test(value)) {
        const timestampTime = DateTime.fromSeconds(Number(value), timezoneOption);
        return formatPeriod(timestampTime, fullTimestampPrecision ? 'second' : 'day');
      }

      // Timestamp in milliseconds.
      if (/^\d{13}$/.test(value)) {
        const timestampTime = DateTime.fromMillis(Number(value), timezoneOption);
        return formatPeriod(timestampTime, fullTimestampPrecision ? 'millisecond' : 'day');
      }

      return null;
    }
  }
}
